import { useTranslation } from "react-i18next";

import { Switch } from "@/components/ui/switch";

import type { TableColumnMeta } from "@/types";

import { DebouncedInput } from "./debounced-input";
import { SearchSelectFilter } from "./search-select-filter";
import { SimpleSelect } from "./simple-select";

/** `Filters` jadval ustunidan faqat shu uchtasini o'qiydi — test ham shuni beradi. */
interface ColumnType {
  getFilterValue: () => unknown;
  setFilterValue: (value: unknown) => void;
  columnDef: {
    meta?: TableColumnMeta;
  };
}

/**
 * Ustun sarlavhasidagi filtr.
 *
 * Qaysi boshqaruv chizilishi ustunning `meta.filterVariant` iga bog'liq —
 * jadval komponenti filtrlar ro'yxatini bilmaydi va bilishi ham shart emas.
 * Yangi ustun qo'shilganda faqat `meta` yoziladi.
 */
export function Filters({ column, label }: Readonly<{ column: ColumnType; label?: string }>) {
  const { t } = useTranslation();

  const value = column.getFilterValue();
  const setValue = column.setFilterValue;
  const meta = column.columnDef.meta;
  const variant = meta?.filterVariant;

  if (!variant) return null;

  switch (variant) {
    case "search-select": {
      if (!meta?.filterOptions && !meta?.filterOptionsAsync) return null;

      return (
        <SearchSelectFilter
          value={typeof value === "string" || typeof value === "number" ? String(value) : undefined}
          onChange={setValue}
          options={meta.filterOptions}
          getQuery={meta.filterOptionsAsync}
          getLabel={meta.filterOptionLabel}
        />
      );
    }

    case "text":
      return (
        <DebouncedInput
          type="text"
          aria-label={label}
          placeholder={t("TABLE.SEARCH")}
          value={typeof value === "string" || typeof value === "number" ? value : ""}
          onChange={next => setValue(next)}
        />
      );

    case "select": {
      const options = meta?.filterOptions;
      if (!options) return null;

      return (
        <SimpleSelect
          aria-label={label}
          placeholder={t("TABLE.SELECT")}
          value={value as string | number | boolean | null | undefined}
          onChange={setValue}
          options={options}
        />
      );
    }

    case "toggle": {
      const toggleLabel = meta?.filterLabel ?? label;
      return (
        <div className="flex items-center gap-2 p-3">
          <Switch
            aria-label={toggleLabel ?? t("TABLE.FILTER")}
            checked={Boolean(value)}
            onCheckedChange={setValue}
          />
          {toggleLabel && <span className="text-sm">{toggleLabel}</span>}
        </div>
      );
    }

    default:
      return null;
  }
}
